import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CAT_MAP } from "@/lib/categories";
import { BookOpen, ArrowRight } from "lucide-react";
import { Seo, breadcrumbLd } from "@/components/Seo";

export default function GuidesPage() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/guides").then((res) => setGuides(res.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <Seo
        title="Guide al collezionismo"
        description="Guide pratiche ed evergreen per iniziare a collezionare, valutare e conservare: carte, orologi, auto d'epoca, monete, francobolli, fumetti, sneakers, LEGO, arte e memorabilia."
        path="/guide"
        jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "Guide", path: "/guide" }])}
      />
      <div className="flex items-center gap-2 mb-3">
        <BookOpen size={16} className="text-gold" />
        <span className="font-meta text-[10px] uppercase tracking-[0.24em] text-stone-500">Approfondimenti</span>
      </div>
      <h1 className="font-editorial font-extrabold text-4xl sm:text-5xl tracking-tight text-stone-900">Guide al <span className="mark-hl">Collezionismo</span></h1>
      <p className="text-stone-600 mt-4 max-w-2xl">
        Guide evergreen per iniziare, valutare e collezionare con consapevolezza in ogni categoria.
      </p>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10 animate-pulse">
          {[...Array(4)].map((_, i) => <div key={i} className="h-64 rounded-xl bg-panel" />)}
        </div>
      ) : guides.length === 0 ? (
        <p className="text-stone-500 py-16 text-center">Nessuna guida disponibile per ora.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {guides.map((g) => {
            const cat = CAT_MAP[g.category];
            return (
              <Link key={g.id} to={g.canonical_path || `/guida/${g.slug}`} data-testid="guide-card"
                className={`group flex flex-col rounded-2xl overflow-hidden bg-panel border border-line lift border-l-[6px] ${cat?.accent}`}>
                <div className="overflow-hidden aspect-[16/10] bg-ink-deep">
                  <img src={g.image_url} alt={g.title} className="w-full h-full object-cover opacity-100 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-700" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <span className={`inline-flex w-fit items-center gap-1.5 px-2.5 py-1 text-[10px] font-meta font-semibold uppercase tracking-[0.12em] border rounded-full ${cat?.badge}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${cat?.dot}`} />{cat?.name}
                  </span>
                  <h3 className="font-editorial font-bold text-xl leading-snug text-stone-900 mt-3 group-hover:text-gold transition-colors">{g.title}</h3>
                  <p className="text-sm text-stone-600 mt-2 line-clamp-2 flex-1">{g.intro}</p>
                  <span className="inline-flex items-center gap-1 font-meta text-[11px] font-semibold uppercase tracking-wide text-gold mt-4">Leggi la guida <ArrowRight size={13} /></span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
